import { useState, useCallback, useEffect } from 'react';
import {
  BarChart3, DollarSign, TrendingUp, Shield,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { reportApi } from '../services/reportApi';
import { useCompany } from '../contexts/CompanyContext';
import { chatTheme as th } from '../theme/chat';
import AgentPageLayout from '../components/shared/AgentPageLayout';
import ItemSidebar from '../components/shared/ItemSidebar';
import Workspace from '../components/shared/Workspace';
import DataTable from '../components/shared/DataTable';
import ReportForm from '../components/reports/ReportForm';
import ReconciliationPanel from '../components/reports/ReconciliationPanel';
import ReportChat from '../components/reports/ReportChat';

// ── Template definitions ──────────────────────────────────────────────────────

const TEMPLATES = [
  {
    type: 'pnl',
    name: 'Profit & Loss',
    description: 'Revenue, expenses, and net income',
    icon: DollarSign,
    color: th.templates.pnl.color,
    bg: th.templates.pnl.bg,
  },
  {
    type: 'balance_sheet',
    name: 'Balance Sheet',
    description: 'Assets, liabilities, and equity',
    icon: BarChart3,
    color: th.templates.balance_sheet.color,
    bg: th.templates.balance_sheet.bg,
  },
  {
    type: 'cash_flow',
    name: 'Cash Flow',
    description: 'Operating, investing, and financing',
    icon: TrendingUp,
    color: th.templates.cash_flow.color,
    bg: th.templates.cash_flow.bg,
  },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  // State
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [activeReportType, setActiveReportType] = useState(null);
  const [reportResult, setReportResult] = useState(null);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [parameters, setParameters] = useState({
    date_range: ['2025-01-01', '2025-12-31'],
    granularity: 'month',
    accounting_method: 'Accrual',
    dimension_splits: [],
    filters: {},
  });
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState(null);

  // Companies state
  const { selectedCompanies, addCompany, removeCompany, setCompanies, rfIds } = useCompany();
  const [activeThreadId, setActiveThreadId] = useState(null);

  // Chat item name for sidebar header
  const [chatItemName, setChatItemName] = useState('');

  // ── Load reports list ───────────────────────────────────────────────────────
  const loadReports = useCallback(async () => {
    try {
      const data = await reportApi.listReports('default');
      const list = Array.isArray(data) ? data : (data.items || data.data || []);
      setReports(list);
    } catch (err) {
      console.error('Failed to load reports:', err);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  // ── Select a template → execute-by-type ─────────────────────────────────────
  const handleSelectTemplate = useCallback(async (reportType) => {
    setActiveReport(null);
    setActiveReportType(reportType);
    setReportResult(null);
    setReconcileResult(null);
    setError(null);
  }, []);

  // ── Select a saved report ───────────────────────────────────────────────────
  const handleSelectReport = useCallback(async (id) => {
    setError(null);
    setReportResult(null);
    setReconcileResult(null);
    try {
      const report = await reportApi.getReport(id);
      const data = report.data || report;
      setActiveReport(data);
      setActiveReportType(data.report_type);
      if (data.parameters) {
        setParameters(prev => ({ ...prev, ...data.parameters }));
      }
      if (data.rf_ids?.length) {
        // Preserve existing company names — don't overwrite with rf_id-only data
        const nameMap = new Map(selectedCompanies.map(c => [c.rf_id, c.company_name]));
        setCompanies(data.rf_ids.map(rfId => ({
          rf_id: rfId,
          company_name: nameMap.get(rfId) || rfId,
          source_type: 'QUICKBOOKS',
        })));
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Failed to load report');
    }
  }, [setCompanies, selectedCompanies]);

  // ── Delete a saved report ───────────────────────────────────────────────────
  const handleDeleteReport = useCallback(async (id) => {
    try {
      await reportApi.deleteReport(id);
      if (activeReport?.id === id) {
        setActiveReport(null);
        setActiveReportType(null);
        setReportResult(null);
        setReconcileResult(null);
      }
      await loadReports();
    } catch (err) {
      console.error('Failed to delete report:', err);
    }
  }, [activeReport, loadReports]);

  // ── Chat open/close handlers ────────────────────────────────────────────────
  const handleChatOpen = useCallback((itemId) => {
    if (itemId) {
      const report = reports.find(r => r.id === itemId);
      setChatItemName(report?.name || 'Report');
    } else {
      const tmplName = activeReportType
        ? TEMPLATES.find(t => t.type === activeReportType)?.name
        : null;
      setChatItemName(tmplName || 'New Chat');
    }
  }, [reports, activeReportType]);

  const handleChatClose = useCallback(() => {
    setChatItemName('');
  }, []);

  // ── Run report ──────────────────────────────────────────────────────────────
  const handleRun = useCallback(async () => {
    if (!activeReportType && !activeReport) return;
    if (!rfIds.length && !activeReport?.rf_ids?.length) {
      setError('Please enter at least one company rf_id');
      return;
    }

    setLoading(true);
    setError(null);
    setReportResult(null);
    try {
      let result;
      if (activeReport?.id) {
        // Use current CompanyContext rfIds if available, allowing user to change companies
        const execRfIds = rfIds.length ? rfIds : activeReport.rf_ids;
        result = await reportApi.executeReport(activeReport.id, parameters, execRfIds);
      } else {
        result = await reportApi.executeByType({
          report_type: activeReportType,
          rf_ids: rfIds,
          parameters,
        });
      }

      const data = result.data || result;
      const steps = data.steps || data.step_results || [];
      const firstStep = Array.isArray(steps) ? steps[0] : null;
      if (firstStep) {
        const cols = firstStep.columns || [];
        const objRows = (firstStep.rows || []).map(row =>
          Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
        );
        setReportResult({ columns: cols, rows: objRows });
      } else if (data.columns && data.rows) {
        const cols = data.columns;
        const objRows = (data.rows || []).map(row =>
          Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
        );
        setReportResult({ columns: cols, rows: objRows });
      }
    } catch (err) {
      console.error('Failed to execute report:', err);
      setError('Failed to execute report: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [activeReport, activeReportType, rfIds, parameters]);

  // ── Reconcile ───────────────────────────────────────────────────────────────
  const handleReconcile = useCallback(async () => {
    if (!activeReportType && !activeReport) return;
    if (!rfIds.length && !activeReport?.rf_ids?.length) {
      setError('Please enter at least one company rf_id');
      return;
    }

    setReconciling(true);
    setError(null);
    setReconcileResult(null);
    try {
      let result;
      if (activeReport?.id) {
        result = await reportApi.reconcileReport(activeReport.id, { parameters });
      } else {
        result = await reportApi.reconcileByType({
          report_type: activeReportType,
          rf_ids: rfIds,
          parameters,
        });
      }
      const data = result.data || result;
      setReconcileResult(data);
    } catch (err) {
      console.error('Failed to reconcile:', err);
      setError('Failed to reconcile: ' + err.message);
    } finally {
      setReconciling(false);
    }
  }, [activeReport, activeReportType, rfIds, parameters]);

  // ── Parameter change handler ────────────────────────────────────────────────
  const handleParameterChange = useCallback((key, value) => {
    setParameters(prev => ({ ...prev, [key]: value }));
  }, []);

  // ── Workspace action handler (from chat agent) ─────────────────────────────
  const handleWorkspaceAction = useCallback(async (payload) => {
    const action = payload?.action;
    if (action === 'reload_view') {
      const viewId = payload?.view_id;
      if (viewId) {
        await handleSelectReport(viewId);
        await handleRun();
        await loadReports();
      }
    } else if (action === 'show_report') {
      // Fetch the cached execution result — the agent already executed this report
      const viewId = payload?.view_id;
      if (viewId) {
        await handleSelectReport(viewId);
        try {
          const DATA_ENGINE = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
          const r = await fetch(`${DATA_ENGINE}/api/reports/execution-latest/${viewId}`);
          if (r.ok) {
            const json = await r.json();
            const data = json.data || json;
            const steps = data.steps || data.step_results || [];
            const firstStep = Array.isArray(steps) ? steps[0] : null;
            if (firstStep) {
              const cols = firstStep.columns || [];
              const objRows = (firstStep.rows || []).map(row =>
                Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
              );
              setReportResult({ columns: cols, rows: objRows });
            }
            // Update parameters from cached execution
            if (data.parameters) {
              setParameters(prev => ({ ...prev, ...data.parameters }));
            }
          } else {
            // Fallback: re-execute if no cached result
            await handleRun();
          }
        } catch (err) {
          console.error('Failed to fetch cached execution:', err);
          await handleRun();
        }
        await loadReports();
      }
    } else if (action === 'show_reconciliation') {
      // Fetch cached reconciliation result by report ID — agent doesn't pass data
      const viewId = payload?.view_id;
      if (viewId) {
        try {
          const DATA_ENGINE = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
          const r = await fetch(`${DATA_ENGINE}/api/reports/reconcile-latest/${viewId}`);
          if (r.ok) {
            const json = await r.json();
            setReconcileResult(json.data || json);
          }
        } catch (err) {
          console.error('Failed to fetch reconciliation result:', err);
        }
      } else if (payload?.data) {
        // Fallback: accept data directly for backward compat
        setReconcileResult(payload.data);
      }
    } else if (action === 'refresh_view_list') {
      await loadReports();
    } else if (action === 'clear_workspace') {
      setActiveReport(null);
      setActiveReportType(null);
      setReportResult(null);
      setReconcileResult(null);
    }
  }, [handleSelectReport, handleRun, loadReports]);

  // ── Derive title ────────────────────────────────────────────────────────────
  const reportTitle = activeReport?.name ||
    TEMPLATES.find(t => t.type === activeReportType)?.name ||
    'Report';

  return (
    <AgentPageLayout
      sidebar={
        <ItemSidebar
          title="Reports"
          templates={TEMPLATES}
          items={reports}
          activeId={activeReport?.id}
          onSelectTemplate={handleSelectTemplate}
          onSelectItem={handleSelectReport}
          onDeleteItem={handleDeleteReport}
          chat={
            <ReportChat
              selectedCompanies={selectedCompanies}
              onSelectCompany={addCompany}
              onRemoveCompany={removeCompany}
              onWorkspaceAction={handleWorkspaceAction}
              onThreadCreated={setActiveThreadId}
              threadId={activeThreadId}
            />
          }
          onChatOpen={handleChatOpen}
          onChatClose={handleChatClose}
          chatItemName={chatItemName}
        />
      }
    >
      <Workspace
        title={reportTitle}
        onRun={handleRun}
        runLabel="Run Report"
        loading={loading}
        actions={
          <button
            onClick={handleReconcile}
            disabled={reconciling || loading}
            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-zinc-300 text-zinc-600 rounded-md hover:bg-zinc-50 transition-colors disabled:opacity-40"
          >
            <Shield size={12} />
            {reconciling ? 'Reconciling...' : 'Reconcile'}
          </button>
        }
        toolbar={
          <ReportForm
            parameters={parameters}
            onParameterChange={handleParameterChange}
            companyCount={selectedCompanies.length}
          />
        }
        footer={reconcileResult && <ReconciliationPanel result={reconcileResult} />}
      >
        {error && (
          <div className="mx-4 mt-2 px-3 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded">
            {error}
          </div>
        )}
        <DataTable
          columns={reportResult?.columns || []}
          rows={reportResult?.rows || []}
          mode="financial"
          title={reportTitle}
          loading={loading}
          emptyMessage="Select a template or saved report, then click Run to see results."
        />
      </Workspace>
    </AgentPageLayout>
  );
}
